import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Heart, Bookmark, Share2 } from 'lucide-react';

const API_BASE_URL = 'https://newsnine-96d30eb04b38.herokuapp.com';

const ArticleInteractions = ({ articleId, user, articleUrl }) => {
  const [isLiked, setIsLiked] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);

  const createUserIfNotExists = useCallback(async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId })
      });
      if (response.ok) {
        console.log('User created or already exists');
      } else {
        console.error('Error creating user:', response.status);
      }
    } catch (error) {
      console.error('Error creating user:', error);
    }
  }, []);

  const fetchUserInteractions = useCallback(async () => {
    if (!user) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/interactions?user_id=${user.uid}`);
      if (response.ok) {
        const data = await response.json();
        setIsLiked(data.liked_articles.includes(articleId));
        setIsSaved(data.saved_articles.includes(articleId));
      } else {
        console.error('Error fetching user interactions:', response.status);
      }
    } catch (error) {
      console.error('Error fetching user interactions:', error);
    }
  }, [user, articleId]);

  const fetchArticleStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/article/stats?article_id=${articleId}`);
      if (response.ok) {
        const data = await response.json();
        setLikeCount(data.like_count);
      } else {
        console.error('Error fetching article stats:', response.status);
      }
    } catch (error) {
      console.error('Error fetching article stats:', error);
    }
  }, [articleId]);

  useEffect(() => {
    if (user) {
      createUserIfNotExists(user.uid);
      fetchUserInteractions();
      fetchArticleStats();
    }
  }, [user, articleId, createUserIfNotExists, fetchUserInteractions, fetchArticleStats]);

  const handleLike = async () => {
    if (!user) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/article/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.uid, article_id: articleId })
      });
      if (response.ok) {
        const result = await response.json();
        setIsLiked(result.is_liked);
        setLikeCount(result.like_count);
      } else {
        console.error('Error liking article:', response.status);
      }
    } catch (error) {
      console.error('Error liking article:', error);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/article/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.uid, article_id: articleId })
      });
      if (response.ok) {
        const result = await response.json();
        setIsSaved(result.is_saved);
      } else {
        console.error('Error saving article:', response.status);
      }
    } catch (error) {
      console.error('Error saving article:', error);
    }
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(articleUrl);
      setShowCopiedMessage(true);
      setTimeout(() => setShowCopiedMessage(false), 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
  };

  return (
    <div className="article-interactions">
      <button onClick={handleLike} className={`interaction-btn ${isLiked ? 'liked' : ''}`} title={`${likeCount} likes`}>
        <Heart size={30} fill={isLiked ? "#ff6b6b" : "none"} color={isLiked ? "#ff6b6b" : "currentColor"} />
      </button>
      <button onClick={handleSave} className={`interaction-btn ${isSaved ? 'saved' : ''}`}>
        <Bookmark size={30} fill={isSaved ? "#feca57" : "none"} color={isSaved ? "#feca57" : "currentColor"} />
      </button>
      <button onClick={handleShare} className="interaction-btn">
        <Share2 size={30} />
      </button>
      {showCopiedMessage && (
        <div className="copied-message">URL copied to clipboard!</div>
      )}
    </div>
  );
};

ArticleInteractions.propTypes = {
  articleId: PropTypes.string.isRequired,
  user: PropTypes.shape({
    uid: PropTypes.string.isRequired
  }),
  articleUrl: PropTypes.string.isRequired,
};

export default ArticleInteractions;